import { createContext, useContext } from "react";

import { noop } from "ts-essentials";

import {
  Condition,
  EventCondition,
  FilterableColumn,
  InlineTraitCondition,
  NumberOfCondition,
  PropertyCondition,
} from "src/types/visual";
import { AudienceReferenceStats } from "src/graphql";
import type { FilterRestrictions } from "src/components/explore/restrictions";
import type { QueryBuilderVariant } from "src/components/explore/visual/constants";
import { Updater } from "src/utils/immutable-helpers";
import type { StaleWarehouseConditionKind } from "src/components/audiences/utils";

export type SelectedCondition = {
  condition:
    | PropertyCondition
    | InlineTraitCondition
    | EventCondition
    | NumberOfCondition;
  update: (updater: Updater<Condition>) => void;
} | null;

type QueryBuilderContextType = {
  audienceReferenceStats: AudienceReferenceStats | undefined;
  rootModelId: string | null;
  parentModelSemanticType: string | null;
  /**
   * If selectedEvent is specified, its properties may be selected from the query builder and
   * formatted as property conditions.
   * */
  selectedEvent: { eventModelId: number; relationshipId: number } | null;
  selectedCondition: SelectedCondition;
  selectCondition: (input: SelectedCondition) => void;
  /** Temporary state. Remove when referencing entry event is fully supported. */
  enableReferencingEntryEvent: boolean;
  enableNestedFiltering?: boolean;
  maxNestingDepth: number;
  /** How densely condition rows render. `compact` also disables drag-and-drop,
   * since the left rail it moves the AND/OR toggle into is the drag handle. */
  variant: QueryBuilderVariant;
  /** Filter features the consuming surface doesn't support. */
  restrictions?: FilterRestrictions;
  filterableColumnsOverride?: FilterableColumn[];
  enableRealtime: boolean;
  audienceId?: string | number | null;
  /**
   * Warehouse-relevant leaf conditions that have been added or modified
   * since the audience's last successful warehouse pull, keyed by id.
   * The realtime audience UI uses this to render per-condition
   * staleness warnings. Empty for non-realtime audiences.
   */
  staleWarehouseConditions: Map<string, StaleWarehouseConditionKind>;
};

const defaultContextValue: QueryBuilderContextType = {
  audienceReferenceStats: undefined,
  rootModelId: null,
  parentModelSemanticType: null,
  selectedCondition: null,
  selectedEvent: null,
  selectCondition: noop,
  enableReferencingEntryEvent: false,
  maxNestingDepth: 2,
  variant: "default",
  enableRealtime: false,
  staleWarehouseConditions: new Map(),
};

export const QueryBuilderContext =
  createContext<QueryBuilderContextType>(defaultContextValue);
QueryBuilderContext.displayName = "QueryBuilderContext";

export const useQueryBuilderContext = () => useContext(QueryBuilderContext);
